import { useState } from 'react'
import { Plus } from 'lucide-react'
import type { IsbtPrinter } from '@/api/labelPrintingApi'
import { useIsbtPrinters, useIsbtTemplates, useLabelPrintingOptions } from '@/hooks/useLabelPrinting'
import { allowedTemplatesText, locationSuggestions, printerStatus } from '@/lib/labelPrinting'
import type { TableColumnDef } from '@/lib/tableColumnPrefs'
import type { SortAccessors } from '@/lib/tableSort'
import { Button } from '@/components/ui/button'
import { TableCell } from '@/components/ui/table'
import { IsbtPrinterDialog } from './IsbtPrinterDialog'
import { ListSection, ListTable, NameCell, StatusChip } from './shared'

const TABLE_ID = 'label-printing-isbt-printers'

const COLUMNS: TableColumnDef[] = [
  { id: 'displayName', header: 'Display name', locked: true, defaultWidth: 230, sortable: true },
  { id: 'queueName', header: 'Queue name', defaultWidth: 230, sortable: true },
  { id: 'loadedStock', header: 'Loaded stock', defaultWidth: 120, sortable: true },
  { id: 'allowed', header: 'Allowed templates', defaultWidth: 260 },
  { id: 'location', header: 'Location', defaultWidth: 200, sortable: true },
  { id: 'status', header: 'Status', defaultWidth: 190, sortable: true },
]

const SORT: SortAccessors<IsbtPrinter> = {
  displayName: p => p.displayName,
  queueName: p => p.queueName,
  loadedStock: p => p.loadedStock,
  location: p => p.location,
  status: p => printerStatus(p).label,
}

const allowedText = (p: IsbtPrinter) =>
  allowedTemplatesText(p.allowedTemplates.map(t => ({ name: t.displayName, active: t.active })))

/**
 * ISBT printers (z8nrz7evkc): each reached by its queue name on the print
 * computer, never by a network address.
 */
export function IsbtPrinterList({
  canManage,
  onNotice,
}: {
  canManage: boolean
  onNotice: (message: string) => void
}) {
  const query = useIsbtPrinters()
  const templates = useIsbtTemplates()
  const options = useLabelPrintingOptions()
  const [open, setOpen] = useState<{ id: string | null } | null>(null)
  const editing = open?.id ? query.data?.find(p => p.id === open.id) : undefined

  return (
    <>
      <ListSection
        title='ISBT Printers'
        blurb='One row per printer set up on the print computer. Smart CGT reaches each one by its queue name, never by a network address.'
        aside={
          canManage && (
            <Button onClick={() => setOpen({ id: null })}>
              <Plus className='mr-2 h-4 w-4' />
              Add printer
            </Button>
          )
        }
        footnote='A test destination, such as a PDF printer for validation, is just another printer.'
        query={query}
        noun='printers'
      >
        {rows => (
          <ListTable
            tableId={TABLE_ID}
            columns={COLUMNS}
            sort={SORT}
            rows={rows}
            cells={p => ({
              displayName: (
                <TableCell>
                  <NameCell name={p.displayName} onOpen={canManage ? () => setOpen({ id: p.id }) : undefined} />
                </TableCell>
              ),
              queueName: <TableCell>{p.queueName}</TableCell>,
              loadedStock: <TableCell>{p.loadedStock}</TableCell>,
              allowed: <TableCell className='text-muted-foreground'>{allowedText(p) || '—'}</TableCell>,
              location: <TableCell className='text-muted-foreground'>{p.location || '—'}</TableCell>,
              status: (
                <TableCell>
                  <StatusChip {...printerStatus(p)} />
                </TableCell>
              ),
            })}
          />
        )}
      </ListSection>

      {open && (open.id === null || editing) && (
        <IsbtPrinterDialog
          key={open.id ?? 'new'}
          printer={editing}
          templates={templates.data ?? []}
          locations={locationSuggestions(options.data?.locations ?? [], (query.data ?? []).map(p => p.location))}
          onClose={() => setOpen(null)}
          onDone={message => {
            setOpen(null)
            onNotice(message)
          }}
        />
      )}
    </>
  )
}
